import { useMemo } from 'react';
import { isFuture, isToday } from 'date-fns';
import { CalendarIcon, CalendarSearchIcon, CalendarX2Icon } from 'lucide-react';

import { useCustomerBookingCtx } from '@/app/(app)/customers/[id]/customer-booking-provider';
import { useCustomerCtx } from '@/app/(app)/customers/[id]/provider';
import { DateTime, TimeInUTC } from '@/components/atoms/datetime';
import { Tabs } from '@/components/atoms/tabs';
import { EmployeeAvatar } from '@/components/molecules/employee-avatar';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useEmployeeById } from '@/components/providers/v1-office-hooks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

type ServiceEventWithRoute = ApiSchemas['CustomerServiceEventRead'];

export function CustomerAppointments() {
  const { customer } = useCustomerCtx();

  return (
    <Tabs
      defaultValue='upcoming'
      options={[
        { value: 'upcoming', label: 'Upcoming' },
        { value: 'past', label: 'Past' },
      ]}
      rootClassName='flex flex-col gap-4'
    >
      <TabsContent value='upcoming'>
        <AppointmentsList serviceEvents={customer.upcoming_events} emptyMessage='No upcoming appointments' />
      </TabsContent>
      <TabsContent value='past'>
        <AppointmentsList serviceEvents={customer.past_events} emptyMessage='No past appointments' />
      </TabsContent>
    </Tabs>
  );
}

interface AppointmentsListProps {
  serviceEvents: ServiceEventWithRoute[];
  emptyMessage: string;
}

function AppointmentsList({ serviceEvents, emptyMessage }: AppointmentsListProps) {
  if (serviceEvents.length === 0) {
    return (
      <div className='flex h-52 items-center justify-center rounded-2xl bg-muted/50 text-center text-sm font-medium text-muted-foreground'>
        {emptyMessage}
      </div>
    );
  }
  return (
    <div className='flex flex-col gap-4'>
      {serviceEvents.map((event) => (
        <AppointmentCard key={event.id} serviceEvent={event} />
      ))}
    </div>
  );
}

interface AppointmentCardProps {
  serviceEvent: ServiceEventWithRoute;
}

function AppointmentCard({ serviceEvent }: AppointmentCardProps) {
  const { api } = useApiV1Ctx();
  const { customer, serviceTypesById, refetch } = useCustomerCtx();
  const { startReschedule, editingServiceEvent } = useCustomerBookingCtx();

  const { serviceType, servicePlan } = useMemo(() => {
    const serviceType = serviceTypesById.get(serviceEvent.service_type_id);
    const servicePlan = customer.service_plans.find((plan) => plan.id === serviceType?.service_plan_id);
    return { serviceType, servicePlan };
  }, [serviceEvent.service_type_id, serviceTypesById, customer.service_plans]);
  const name = serviceType?.name || 'Unknown Service';

  const canEdit = useMemo(() => {
    if (serviceEvent.status === 'PENDING') return true;
    const isConfirmed = serviceEvent.status === 'CONFIRMED';
    const isTodayOrFuture =
      serviceEvent.event?.date && (isToday(serviceEvent.event.date) || isFuture(serviceEvent.event.date));
    return isConfirmed && isTodayOrFuture;
  }, [serviceEvent.status, serviceEvent.event?.date]);

  const cancelMutation = api.serviceEvents.cancelServiceEvent.useMutation(
    {
      path: {
        service_event_id: serviceEvent.id,
      },
    },
    {
      onSuccess: () => {
        refetch();
      },
    }
  );

  const handleCancel = () => {
    if (confirm('Are you sure you want to cancel this appointment?')) {
      cancelMutation.mutate();
    }
  };

  const timeFormatted = useMemo(() => {
    const start_time = serviceEvent.event.window_start_time || serviceEvent.event.start_time;
    const end_time = serviceEvent.event.window_end_time || serviceEvent.event.end_time;
    if (start_time && end_time) {
      return (
        <>
          Between <TimeInUTC value={start_time} format='h:mm a' /> and <TimeInUTC value={end_time} format='h:mm a' />
        </>
      );
    }
    if (start_time) {
      return (
        <>
          Starts at <TimeInUTC value={start_time} format='h:mm a' />
        </>
      );
    }
    if (end_time) {
      return (
        <>
          Ends at <TimeInUTC value={end_time} format='h:mm a' />
        </>
      );
    }
    return 'Time TBD';
  }, [serviceEvent.event]);

  const isSelected = editingServiceEvent?.id === serviceEvent.id;

  return (
    <div className={cn('flex flex-col gap-4 rounded-4xl p-4 shadow-md', isSelected && 'ring ring-primary')}>
      {/* header */}
      <div className='flex items-center gap-3 text-muted-foreground'>
        <CalendarIcon className='size-4 shrink-0' />
        <span className='flex-1 text-base'>
          {name} on <DateTime value={serviceEvent.event.date} format='EEEE, MMM d, yyyy' />
        </span>
        {canEdit && (
          <div className='flex gap-2'>
            <Button size='sm' variant='outline' onClick={() => startReschedule(serviceEvent)} disabled={isSelected}>
              <CalendarSearchIcon className='size-4' />
              Reschedule
            </Button>
            <Button
              size='sm'
              variant='outline'
              onClick={handleCancel}
              disabled={cancelMutation.isPending || isSelected}
            >
              <CalendarX2Icon className='size-4' />
              Cancel
            </Button>
          </div>
        )}
      </div>
      {/* properties */}
      <div className='flex flex-col'>
        <div className='grid grid-cols-[10rem_1fr] items-center gap-2 py-2 not-last:border-b-[0.5px]'>
          <div className='w-40 text-sm text-muted-foreground'>Service Type</div>
          <div className='text-sm'>{name}</div>
        </div>
        {servicePlan && (
          <div className='grid grid-cols-[10rem_1fr] items-center gap-2 py-2 not-last:border-b-[0.5px]'>
            <div className='w-40 text-sm text-muted-foreground'>Service Plan</div>
            <div className='text-sm'>{servicePlan.name}</div>
          </div>
        )}
        <div className='grid grid-cols-[10rem_1fr] items-center gap-2 py-2 not-last:border-b-[0.5px]'>
          <div className='w-40 text-sm text-muted-foreground'>Duration</div>
          <div className='text-sm'>{serviceEvent.event.duration_minutes} minutes</div>
        </div>
        <div className='grid grid-cols-[10rem_1fr] items-center gap-2 py-2 not-last:border-b-[0.5px]'>
          <div className='w-40 text-sm text-muted-foreground'>Time</div>
          <div className='text-sm'>{timeFormatted}</div>
        </div>
        {serviceEvent.note && (
          <div className='grid grid-cols-[10rem_1fr] items-center gap-2 py-2 not-last:border-b-[0.5px]'>
            <div className='w-40 text-sm text-muted-foreground'>Note</div>
            <div className='text-sm'>{serviceEvent.note.content}</div>
          </div>
        )}
        <div className='grid grid-cols-[10rem_1fr] items-center gap-2 py-2 not-last:border-b-[0.5px]'>
          <div className='w-40 text-sm text-muted-foreground'>Status</div>
          <StatusBadge status={serviceEvent.status} />
        </div>
      </div>
      {serviceEvent.event.route && serviceEvent.event.route.employee_id && (
        <AppointmentEmployee employeeId={serviceEvent.event.route.employee_id} />
      )}
    </div>
  );
}

function AppointmentEmployee({ employeeId }: { employeeId: string }) {
  const employee = useEmployeeById(employeeId);

  return (
    <div className='flex items-center gap-3'>
      <EmployeeAvatar employeeId={employeeId} />
      <div className='space-y-1'>
        <div className='text-xs leading-none text-muted-foreground'>Technician</div>
        <div className='text-sm leading-none'>
          {employee?.first_name} {employee?.last_name}
        </div>
      </div>
    </div>
  );
}

interface StatusBadgeProps {
  status: ServiceEventWithRoute['status'];
}

function StatusBadge({ status }: StatusBadgeProps) {
  const getStatusColor = () => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'RESCHEDULED':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'NO_SHOW':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <Badge
      variant='outline'
      className={cn('rounded-lg border-none px-2 py-0.5 text-[10px] font-semibold uppercase', getStatusColor())}
    >
      {status}
    </Badge>
  );
}
